import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import { quotaService, QuotaStatus, DailyUsage } from '../services/quotaService';

interface QuotaDisplayProps {
  refreshInterval?: number; // ミリ秒（デフォルト: 30秒）
  showDetails?: boolean;
}

export const QuotaDisplay: React.FC<QuotaDisplayProps> = ({
  refreshInterval = 30000,
  showDetails = true
}) => {
  const [quotaStatus, setQuotaStatus] = useState<QuotaStatus | null>(null);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQuotaInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [status, daily] = await Promise.all([
        quotaService.getQuotaStatus(),
        quotaService.getDailyUsage()
      ]);
      
      setQuotaStatus(status);
      setDailyUsage(daily);
    } catch (err: any) {
      console.error('クォータ情報取得エラー:', err);
      const errorMessage = err.response?.data?.error || err.message || 'クォータ情報の取得に失敗しました';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 初期ロード
    fetchQuotaInfo();

    // 定期的にリロード
    const interval = setInterval(fetchQuotaInfo, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  if (loading && !quotaStatus) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning">
        {error}
      </Alert>
    );
  }

  if (!quotaStatus) {
    return null;
  }

  // showDetailsが false の場合は、シンプル表示
  if (!showDetails) {
    const progressColor = 
      quotaStatus.usage_percent >= 95 ? '#f44336' :
      quotaStatus.usage_percent >= 80 ? '#ff9800' :
      '#4caf50';

    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(quotaStatus.usage_percent, 100)}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  backgroundColor: progressColor
                }
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold', minWidth: '50px', textAlign: 'right', color: progressColor }}>
            {quotaStatus.usage_percent.toFixed(1)}%
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#666' }}>
          残り: {quotaStatus.remaining.toLocaleString()} / {quotaStatus.limit.toLocaleString()} トークン
        </Typography>
        {quotaStatus.warning && (
          <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
            <Typography variant="caption">
              ⚠️ 無料枠の{quotaStatus.usage_percent.toFixed(0)}%を使用しています
            </Typography>
          </Alert>
        )}
      </Box>
    );
  }

  // showDetailsが true の場合は、詳細表示
  // 使用率に基づいて色を決定
  const getProgressColor = (usagePercent: number): 'inherit' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    if (usagePercent >= 95) return 'error';
    if (usagePercent >= 80) return 'warning';
    return 'success';
  };

  const progressColor = getProgressColor(quotaStatus.usage_percent);

  return (
    <Box sx={{ width: '100%' }}>
      {/* メイン: 使用状況プログレスバー */}
      <Card sx={{ mb: 2, backgroundColor: quotaStatus.critical ? '#ffebee' : quotaStatus.warning ? '#fff3e0' : '#f1f8e9' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            Gemini API 無料枠
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                使用状況: {quotaStatus.used.toLocaleString()} / {quotaStatus.limit.toLocaleString()} トークン
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: progressColor }}>
                {quotaStatus.usage_percent.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(quotaStatus.usage_percent, 100)}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  backgroundColor: 
                    quotaStatus.critical ? '#f44336' :
                    quotaStatus.warning ? '#ff9800' :
                    '#4caf50'
                }
              }}
            />
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#666' }}>
              残り: {quotaStatus.remaining.toLocaleString()} トークン
            </Typography>
          </Box>

          {/* 警告・エラーメッセージ */}
          {quotaStatus.critical && (
            <Alert severity="error" sx={{ mb: 1 }}>
              ⚠️ 無料枠の95%以上を使用しています。API呼び出しが失敗する可能性があります。
            </Alert>
          )}
          {quotaStatus.warning && !quotaStatus.critical && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              ⚠️ 無料枠の80%以上を使用しています。AI抽出機能の利用を削減してください。
            </Alert>
          )}

          {/* 詳細情報 */}
          {showDetails && dailyUsage && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      本日の呼び出し数
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {dailyUsage.total_calls}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      成功率
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: dailyUsage.success_rate >= 95 ? '#4caf50' : dailyUsage.success_rate >= 80 ? '#ff9800' : '#f44336'
                      }}
                    >
                      {dailyUsage.success_rate.toFixed(1)}%
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      本日のトークン
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {(dailyUsage.total_input_tokens + dailyUsage.total_output_tokens).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#999', textAlign: 'center' }}>
                最終更新: {new Date(quotaStatus.last_updated).toLocaleTimeString('ja-JP')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default QuotaDisplay;
